<template>
    <v-container :fluid="true" class="max-height my-5">
        <v-row>
            <h1 class="text-left">Vehicle Utilization Report</h1>
        </v-row>

        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="2" sm="6">
                <company-select v-model="formData['company']" :items="companiesList" density="compact" label="Select Company"
                          @update:model-value="()=>{driversList=[];fetchDriversList();fetchReport();}"/>
            </v-col>
            <v-col cols="12" md="2" sm="6">
                <v-select v-model="formData['driver']" :items="driversList" clearable
                          density="compact" :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" item-value="id"
                          label="Select Driver" @update:model-value="fetchReport"/>
            </v-col>
            <v-col cols="12" md="2" sm="6">
                <v-date-input
                    v-model="formData['start_date']"
                    append-inner-icon="$calendar"
                    density="compact"
                    hide-actions
                    label="Start Date"
                    prepend-icon=""
                    clearable
                    @click:clear="()=>{formData['start_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="2" sm="6">
                <v-date-input
                    v-model="formData['end_date']"
                    append-inner-icon="$calendar"
                    density="compact"
                    hide-actions
                    label="End Date"
                    clearable
                    prepend-icon=""
                    @click:clear="()=>{formData['end_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>

            <v-col cols="auto" class="d-flex align-center">
                <v-btn :loading="downloading" prepend-icon="mdi-download" size="small" variant="tonal"
                       @click="exportCsv">Export CSV</v-btn>
            </v-col>
            <v-col class="float-end" cols="1">
                <v-btn :icon="`mdi-${graphView?'table':'chart-line'}`" size="small" @click="graphView = !graphView"/>
            </v-col>
        </v-row>

        <v-row v-if="!graphView">
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Asset</th>
                        <th>Time on road</th>
                        <th>Idle Time</th>
                        <th>Distance Travelled</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in reportData" v-if="reportData.length" class="text-left">
                        <td>{{ item.asset }}</td>
                        <td>{{ formatSecondsToReadableTime(item.driving_time) }}</td>
                        <td>{{ formatSecondsToReadableTime(item.idle_time) }}</td>
                        <td>{{ strOperations().unitifyDistance(item.distance_km, 'distance', 'km') }}</td>
                    </tr>
                    <tr v-else>
                        <td v-if="processing" class="text-center" colspan="4">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="4">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>

        </v-row>
        <v-row v-else>
            <v-col cols="12">
                <Bar :data="chartData" :options="chartOptions" height="100"/>
            </v-col>
        </v-row>
        <div class="my-5">&nbsp;</div>
    </v-container>
</template>

<script setup>
import {Bar} from "vue-chartjs";
import {useDate} from 'vuetify';
import {computed, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js'
import {useAuthStore} from "@/stores/authStore";
import {strOperations} from "@/composables/globals";
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useReportExport} from "@/composables/useReportExport";
import CompanySelect from "@/components/globals/company-select.vue";


Chart.register(...registerables)


const authStore = useAuthStore();
const {user} = authStore;
const {downloadReport} = useReportExport();
const graphView = ref(true);
const processing = ref(false);
const downloading = ref(false);
const reportData = ref([]);
const driversList = ref([]);
const companiesList = ref([]);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    company: ref(user.type !== 'admin' ? user.company.id : null),
    start_date: null,
    end_date: null,
    driver: null,
})

onMounted(() => {
    // const adapter = useDate();
    // const today = new Date();
    // const sevenDaysAgo = new Date();
    // sevenDaysAgo.setDate(today.getDate() - 7);
    // formData['start_date'] = adapter.parseISO(sevenDaysAgo.toISOString().split('T')[0]);
    // formData['end_date'] = adapter.parseISO(today.toISOString().split('T')[0]);

    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchReport();
        fetchDriversList();
    }

})

const distanceUnit = computed(() => authStore.configs?.distance_unit === 'km' ? 'km' : 'mi');

const chartOptions = computed(() => ({
    responsive: true,
    scales: {
        x: {
            stacked: true,
        },
        y: {
            stacked: true,
            title: {display: true, text: 'Time (hrs)'},
        },
        y1: {
            position: 'right',
            title: {display: true, text: `Distance (${distanceUnit.value})`},
            grid: {drawOnChartArea: false}
        }
    },
    plugins: {
        tooltip: {
            callbacks: {
                label: ctx => {
                    const label = ctx.dataset.label || ''
                    const value = ctx.raw
                    return `${label}: ${label.includes('Time') ? value + ' hrs' : value + ' ' + distanceUnit.value}`
                }
            }
        }
    }
}));

const chartData = computed(() => {
    const labels = [];
    const idleData = [];
    const drivingData = [];
    const distanceData = [];

    reportData.value.map((item) => {
        labels.push(item.asset);
        distanceData.push(parseFloat(strOperations().unitifyDistance(item.distance_km, '', 'km')));
        idleData.push(parseFloat(item.idle_time / 3600).toFixed(2));
        drivingData.push(parseFloat(item.driving_time / 3600).toFixed(2));
    });

    return {
        labels,
        datasets: [
            {
                label: 'Idle Time (hrs)',
                backgroundColor: '#E91E63',
                data: idleData,
                stack: 'time',
            },
            {
                label: 'Time on Road (hrs)',
                backgroundColor: '#673AB7',
                data: drivingData,
                stack: 'time',
            },
            {
                label: 'Distance (km)',
                backgroundColor: '#009688',
                data: distanceData,
                yAxisID: 'y1',
                type: 'line',
            }
        ]
    }
})


const formatSecondsToReadableTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)

    return `${hrs}h ${mins}m`
}

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companiesList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchDriversList = () => {
    send('get', `companies/drivers?list=true`, {company: formData['company']})
        .then((response) => {
            driversList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
const fetchReport = () => {
    if (!formData['company']) {
        showSnackbar('Company is required', 'error')
        return
    }
    processing.value = true;
    send('get', `fleet/reports/vehicle-utilization-report`, formData)
        .then((response) => {
            reportData.value = response.data.data;
        })
        .catch((error) => {
            if (error.response.data.errors) {
                let validationErrors = error.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    showSnackbar(validationErrors[key][0], 'error')
                });
            } else if (error.response.data.message) {
                showSnackbar(error.response.data.message ?? 'Summary report could not be loaded', 'error')
            }
        }).finally(() => processing.value = false)
}

const exportCsv = async () => {
    if (!formData['company']) {
        showSnackbar('Company is required', 'error')
        return
    }
    downloading.value = true
    try {
        await downloadReport('fleet/reports/vehicle-utilization-report', formData, 'csv', 'vehicle-utilization.csv')
    } catch (e) {
        showSnackbar('Export failed', 'error')
    } finally {
        downloading.value = false
    }
}
</script>
